'use client';

import {
  useEffect,
  useState,
  useRef,
  useCallback,
  MutableRefObject
} from 'react';
import type Plyr from 'plyr';

const JUMP_EVENT = 'video-jump-timestamp';

export interface UseVideoTimestampJumpOptions {
  /** Whether the video component is currently showing/mounted */
  isVideoShowing: boolean;
  /** Whether Plyr is ready to accept commands */
  isPlyrReady: boolean;
  /** Reference to the Plyr instance */
  plyrRef: MutableRefObject<Plyr | null>;
  /** Reference to the underlying video element */
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  /** Called when a jump is requested but video isn't showing yet */
  onStartVideo?: () => void;
}

/**
 * Hook for handling video timestamp jump events.
 * Listens for 'video-jump-timestamp' CustomEvents and either:
 * - Jumps directly if video is showing and Plyr is ready
 * - Stores pending jump and triggers video start if not showing
 * - Handles pending jump when Plyr becomes ready
 */
export function useVideoTimestampJump(
  options: UseVideoTimestampJumpOptions
): void {
  const { isVideoShowing, isPlyrReady, plyrRef, videoRef, onStartVideo } =
    options;

  const [pendingJumpSeconds, setPendingJumpSeconds] = useState<number | null>(
    null
  );
  const pendingJumpProcessedRef = useRef(false);

  // Direct jump function
  const performJump = useCallback(
    (seconds: number) => {
      const player = plyrRef.current;
      if (!player || !isPlyrReady) return;

      const doSeek = () => {
        player.currentTime = seconds;
        player.play()?.catch((err) => {
          console.warn('[useVideoTimestampJump] Play after jump failed:', err);
        });
      };

      const video = videoRef.current;
      if (!video || video.readyState >= HTMLMediaElement.HAVE_METADATA) {
        doSeek();
      } else {
        video.addEventListener('loadedmetadata', doSeek, { once: true });
      }
    },
    [plyrRef, videoRef, isPlyrReady]
  );

  // Public jump function
  const jumpTo = useCallback(
    (seconds: number) => {
      if (!isVideoShowing) {
        // Video not showing - store pending and start video
        setPendingJumpSeconds(seconds);
        pendingJumpProcessedRef.current = false;
        onStartVideo?.();
      } else if (isPlyrReady) {
        // Video showing and ready - jump directly
        performJump(seconds);
      } else {
        // Video showing but not ready - store pending
        setPendingJumpSeconds(seconds);
        pendingJumpProcessedRef.current = false;
      }
    },
    [isVideoShowing, isPlyrReady, performJump, onStartVideo]
  );

  // Listen for jump events
  useEffect(() => {
    const handleJumpEvent = (event: CustomEvent<{ seconds: number }>) => {
      const { seconds } = event.detail;
      jumpTo(seconds);
    };

    window.addEventListener(JUMP_EVENT, handleJumpEvent as EventListener);
    return () => {
      window.removeEventListener(JUMP_EVENT, handleJumpEvent as EventListener);
    };
  }, [jumpTo]);

  // Handle pending jump when player becomes ready
  useEffect(() => {
    if (
      pendingJumpSeconds !== null &&
      isPlyrReady &&
      plyrRef.current &&
      !pendingJumpProcessedRef.current
    ) {
      pendingJumpProcessedRef.current = true;
      performJump(pendingJumpSeconds);
      setPendingJumpSeconds(null);
    }
  }, [pendingJumpSeconds, isPlyrReady, plyrRef, performJump]);
}
